import React, { useMemo } from "react";

import "react-toastify/dist/ReactToastify.css";
import "./assets/stylesheets/App.scss";
import "./assets/stylesheets/Notifications.scss";

import { createBrowserRouter, RouterProvider } from "react-router-dom";
import { useSelector } from "react-redux";
import { AuthProvider } from "react-oidc-context";

import CustomApolloProvider from "./utils/CustomApolloProvider";
import { oidcConfig } from "./utils/oidcConfig";
import { routerFuture } from "./constants/routerFuture";
import {
  MockModeContext,
  useMockModeManager,
} from "./hooks/useMockModeContext";

const StudentAuthProvider = ({ siteOidc, children }) => {
  const schoolCode = useSelector((state) => state.school.autofillSchoolCode);
  const authProviderProps = useMemo(() => {
    return oidcConfig(siteOidc, "student", schoolCode);
  }, [schoolCode, siteOidc]);

  return (
    <AuthProvider {...authProviderProps}>
      <div data-testid="student-auth-provider">{children}</div>
    </AuthProvider>
  );
};

const FullAuthProvider = ({ siteOidc, children }) => {
  const authProviderProps = useMemo(() => {
    return oidcConfig(siteOidc);
  }, [siteOidc]);

  return (
    <AuthProvider {...authProviderProps}>
      <div data-testid="full-auth-provider">{children}</div>
    </AuthProvider>
  );
};

const AppRouter = ({ ContentComponent }) => {
  const isEmbedded = useSelector((state) => state.editor.isEmbedded);
  return <ContentComponent isEmbedded={isEmbedded} />;
};

const App = ({ ContentComponent, siteOidc }) => {
  const userType = useSelector((state) => state.editor.userType);

  // Initialize mock mode manager (handles all mock state)
  const mockModeManager = useMockModeManager();

  const UserAuthProvider =
    userType === "student" ? StudentAuthProvider : FullAuthProvider;

  const router = useMemo(
    () =>
      createBrowserRouter(
        [
          {
            path: "*",
            element: <AppRouter ContentComponent={ContentComponent} />,
          },
        ],
        { future: routerFuture },
      ),
    [ContentComponent],
  );

  if (!UserAuthProvider) return null;

  return (
    <MockModeContext.Provider value={mockModeManager}>
      <UserAuthProvider siteOidc={siteOidc} userType={userType}>
        <CustomApolloProvider>
          <RouterProvider router={router} future={routerFuture} />
        </CustomApolloProvider>
      </UserAuthProvider>
    </MockModeContext.Provider>
  );
};

export default App;
